import { Estate } from '@/modules/estate/models/Estate';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { User } from '@/modules/user/models/User';
import { UserFlowType } from '@/modules/user/constants/UserFlowType';

export class UserFlow extends BaseEntityModel {
  id?: number;
  estateId?: number;
  userId?: number;
  type?: UserFlowType;
  currentValue?: string;
  isComplete?: boolean;
  isAutostart?: boolean;
  orderedValues!: string[];
  estate?: Relationship<Estate | null>;
  user?: Relationship<User | null>;

  constructor(model: Partial<UserFlow> = {}) {
    super(model);

    this.id = model.id;
    this.estateId = model.estateId;
    this.userId = model.userId;
    this.type = model.type;
    this.currentValue = model.currentValue;
    this.isComplete = model.isComplete;
    this.isAutostart = model.isAutostart;
    this.orderedValues = model.orderedValues ?? [];
    this.estate = model.estate;
    this.user = model.user;
  }

  get progress(): number {
    if (!this.currentValue) {
      return 0;
    }

    if (this.isComplete) {
      return 100;
    }

    return ((this.currentIndex as number) / this.orderedValues.length) * 100;
  }

  get displayProgress(): number {
    return Math.ceil(this.progress);
  }

  get nextValue(): string | undefined {
    if (!this.currentValue) {
      return this.orderedValues[0];
    }

    return this.orderedValues[(this.currentIndex as number) + 1] ?? undefined;
  }

  get previousValue(): string | undefined {
    if (!this.currentValue) {
      return undefined;
    }

    return this.orderedValues[(this.currentIndex as number) - 1] ?? undefined;
  }

  setNextValue(): void {
    const nextFlowItems: string | undefined = this.nextValue;

    if (!nextFlowItems) {
      this.setIsComplete();
    } else {
      this.currentValue = nextFlowItems;
    }
  }

  setInitialValue(): void {
    this.currentValue = this.orderedValues[0];
  }

  setIsComplete(value: boolean = true): void {
    this.isComplete = value;
  }

  setPreviousValue(): void {
    this.currentValue = this.previousValue;
  }

  get currentIndex(): number | undefined {
    if (!this.currentValue) {
      return undefined;
    }

    return this.orderedValues.indexOf(this.currentValue);
  }

  get isFirstSubStep(): boolean {
    return this.currentIndex === 0;
  }

  get isLastSubStep(): boolean {
    return this.currentIndex === this.orderedValues.length - 1;
  }
}
